import { FC, useState, useMemo } from "react"
import {
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  XCircle,
  Mail,
  User
} from "lucide-react"
import { UnpaidLead, BBColumnDef } from "../../../../types"
import {
  useGetUnpaidLeadsQuery,
  useRetryBillingMutation
} from "../../../../redux/queries"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"
import Table from "../../table"
import { PrimaryBtn } from "../../buttons"
import If from "../../if"

interface Props {
  orgId: string
  isAdmin: boolean
}

const UnpaidLeadsTab: FC<Props> = ({ orgId, isAdmin }) => {
  const dispatch = useAppDispatch()
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set())

  const { data: unpaidLeads, isFetching } = useGetUnpaidLeadsQuery(orgId, {
    skip: !orgId
  })

  const [retryBilling, { isLoading: isRetrying }] = useRetryBillingMutation()

  const handleSelectAll = () => {
    if (selectedLeadIds.size === unpaidLeads?.length) {
      setSelectedLeadIds(new Set())
    } else {
      setSelectedLeadIds(new Set(unpaidLeads?.map(l => l.id) ?? []))
    }
  }

  const handleToggleSelect = (leadId: string) => {
    const newSelected = new Set(selectedLeadIds)
    if (newSelected.has(leadId)) {
      newSelected.delete(leadId)
    } else {
      newSelected.add(leadId)
    }
    setSelectedLeadIds(newSelected)
  }

  const handleRetryBilling = async () => {
    if (selectedLeadIds.size === 0) {
      dispatch(
        pushToast({
          msg: "Please select leads to retry billing",
          theme: "warning"
        })
      )
      return
    }

    try {
      const result = await retryBilling(Array.from(selectedLeadIds)).unwrap()

      if (result.status === "success") {
        const successCount = Object.values(result.results).filter(
          r => r.success
        ).length
        const failCount = Object.values(result.results).filter(
          r => !r.success
        ).length

        if (successCount > 0 && failCount === 0) {
          dispatch(
            pushToast({
              msg: `Successfully billed ${successCount} lead${
                successCount > 1 ? "s" : ""
              }`,
              theme: "success"
            })
          )
        } else if (successCount > 0 && failCount > 0) {
          dispatch(
            pushToast({
              msg: `Billed ${successCount} lead${
                successCount > 1 ? "s" : ""
              }, ${failCount} failed`,
              theme: "warning"
            })
          )
        } else {
          dispatch(
            pushToast({
              msg: "Failed to bill selected leads - check balance",
              theme: "danger"
            })
          )
        }

        setSelectedLeadIds(new Set())
      }
    } catch (error) {
      dispatch(
        pushToast({
          msg: "Failed to retry billing",
          theme: "danger"
        })
      )
    }
  }

  const formatDate = (timestamp: string) => {
    const date = new Date(parseInt(timestamp, 10) * 1000)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    })
  }

  const selectColumn: BBColumnDef<UnpaidLead> = {
    id: "select",
    header: () => (
      <input
        type="checkbox"
        checked={
          selectedLeadIds.size === unpaidLeads?.length &&
          unpaidLeads?.length > 0
        }
        onChange={handleSelectAll}
        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
      />
    ),
    accessorFn: row => row.id,
    cell: props => (
      <input
        type="checkbox"
        checked={selectedLeadIds.has(props.row.original.id)}
        onChange={() => handleToggleSelect(props.row.original.id)}
        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
      />
    )
  }

  const baseColumns: BBColumnDef<UnpaidLead>[] = [
    {
      id: "lead",
      header: "Lead",
      accessorFn: row => row.name || row.email,
      cell: props => (
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center">
            <User size={16} className="text-gray-500" />
          </div>
          <div>
            <p className="font-medium text-gray-900">
              {props.row.original.name || "—"}
            </p>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <Mail size={12} />
              {props.row.original.email}
            </p>
          </div>
        </div>
      )
    },
    {
      id: "channel",
      header: "Channel",
      accessorFn: row => row.details.channel_name,
      cell: props => (
        <span className="font-medium text-gray-700">
          {props.row.original.details.channel_name}
        </span>
      )
    },
    {
      id: "cpl",
      header: "CPL",
      accessorFn: row => row.details.cpl_cents,
      cell: props => (
        <span className="font-semibold text-gray-900 tabular-nums">
          ${(props.row.original.details.cpl_cents / 100).toFixed(2)}
        </span>
      )
    },
    {
      id: "status",
      header: "Billing Status",
      accessorFn: row => row.details.billing_status,
      cell: props => {
        const status = props.row.original.details.billing_status
        const statusConfig: Record<
          string,
          { color: string; icon: typeof AlertTriangle }
        > = {
          insufficient_balance: {
            color: "bg-amber-100 text-amber-700",
            icon: AlertTriangle
          },
          billing_failed: {
            color: "bg-red-100 text-red-700",
            icon: XCircle
          },
          pending: {
            color: "bg-blue-100 text-blue-700",
            icon: RefreshCw
          }
        }
        const config = statusConfig[status] || statusConfig.pending
        const Icon = config.icon

        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}
          >
            <Icon size={12} />
            {status.replace(/_/g, " ")}
          </span>
        )
      }
    },
    {
      id: "created",
      header: "Created",
      accessorFn: row => row.timestamp,
      cell: props => (
        <span className="text-sm text-gray-500">
          {formatDate(props.row.original.timestamp)}
        </span>
      )
    }
  ]

  const columns: BBColumnDef<UnpaidLead>[] = useMemo(
    () => (isAdmin ? [selectColumn, ...baseColumns] : baseColumns),
    [selectedLeadIds, unpaidLeads?.length, isAdmin]
  )

  const unpaidCount = unpaidLeads?.length ?? 0

  return (
    <div className="space-y-6">
      {/* Alert Banner */}
      <If
        condition={unpaidCount > 0 && !isFetching}
        then={
          <div className="relative overflow-hidden bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5">
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-100/50 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative flex items-start gap-4">
              <div className="flex-shrink-0 p-2.5 bg-amber-100 rounded-xl">
                <AlertTriangle size={22} className="text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900">
                  {unpaidCount} Unpaid Lead{unpaidCount > 1 ? "s" : ""}
                </h3>
                <p className="text-sm text-amber-700 mt-1">
                  These leads were created but not charged due to insufficient
                  balance.{" "}
                  {isAdmin
                    ? "Add funds and retry billing to process them."
                    : "Contact your administrator to add funds."}
                </p>
              </div>
              {isAdmin && (
                <PrimaryBtn
                  onClick={handleRetryBilling}
                  disabled={selectedLeadIds.size === 0 || isRetrying}
                  className="flex items-center gap-2"
                >
                  <RefreshCw
                    size={16}
                    className={isRetrying ? "animate-spin" : ""}
                  />
                  {isRetrying
                    ? "Retrying..."
                    : `Retry${
                        selectedLeadIds.size > 0
                          ? ` (${selectedLeadIds.size})`
                          : ""
                      }`}
                </PrimaryBtn>
              )}
            </div>
          </div>
        }
      />

      {/* Unpaid Leads Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <If
          condition={isFetching}
          then={
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-gray-400">
                Loading unpaid leads...
              </div>
            </div>
          }
          else={
            <If
              condition={unpaidCount === 0}
              then={
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle size={32} className="text-emerald-500" />
                  </div>
                  <p className="font-semibold text-gray-900 text-lg">
                    All caught up!
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    No unpaid leads at this time
                  </p>
                </div>
              }
              else={
                <Table
                  data={unpaidLeads ?? []}
                  columns={columns}
                  viewType="table"
                  shrinkFilterSort
                  noHeader
                  searchBarVisible={false}
                  defaultSortBy="created"
                  defaultSortDir="desc"
                />
              }
            />
          }
        />
      </div>
    </div>
  )
}

export default UnpaidLeadsTab
